import React, { useEffect, useRef, useState } from "react";
import { BIRTH_DATE_MIN, BIRTH_DATE_MAX } from "../lib/formHelpers";

/* Date de naissance en trois menus déroulants (jour, mois, année) au lieu du calendrier
   du navigateur : sur ordinateur, remonter jusqu'aux années 1980 demandait des dizaines de
   clics (demande de l'école, 10.2026). Entrée et sortie inchangées : une date ISO
   « AAAA-MM-JJ », ou "" tant que les trois menus ne sont pas choisis ou que la date
   n'existe pas (31 février, 29 février d'une année non bissextile). Le choix partiel reste
   affiché : on peut choisir l'année en premier. */

export const MOIS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const deux = (n) => String(n).padStart(2, "0");

// Sans année choisie, février garde son 29 (année bissextile par défaut).
const joursDuMois = (mois, annee) => new Date(Number(annee) || 2000, Number(mois), 0).getDate();

function decouper(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  const mois = m ? Number(m[2]) : 0;
  const jour = m ? Number(m[3]) : 0;
  if (!m || mois < 1 || mois > 12 || jour < 1 || jour > 31) return { jour: "", mois: "", annee: "" };
  return { jour: String(jour), mois: String(mois), annee: m[1] };
}

function assembler({ jour, mois, annee }) {
  if (!jour || !mois || !annee) return "";
  if (Number(jour) > joursDuMois(mois, annee)) return "";
  return `${annee}-${deux(mois)}-${deux(jour)}`;
}

const JOURS = Array.from({ length: 31 }, (_, i) => String(i + 1));

export function ChampNaissance({
  value, onChange, testid, disabled, required,
  min = BIRTH_DATE_MIN, max = BIRTH_DATE_MAX,
  selectClassName = "input-field", label = "Date de naissance",
  // Un menu ignore la hauteur de ligne : même hauteur que les champs .input-field voisins
  // (ligne 1.5em + 2 × 14 px de marge + bordures, mesuré à 54 px sur téléphone).
  selectStyle = { minHeight: "calc(1.5em + 30px)" },
}) {
  const [choix, setChoix] = useState(() => decouper(value));
  // Dernière valeur connue du formulaire (reçue ou émise) : seul un changement venu de
  // l'extérieur (famille connectée, remise à zéro) remplace le choix en cours.
  const derniere = useRef(value || "");
  const refJour = useRef(null);

  useEffect(() => {
    const v = value || "";
    if (v === derniere.current) return;
    derniere.current = v;
    setChoix(decouper(v));
  }, [value]);

  const choisir = (champ) => (e) => {
    const suivant = { ...choix, [champ]: e.target.value };
    setChoix(suivant);
    const iso = assembler(suivant);
    if (iso !== derniere.current) {
      derniere.current = iso;
      onChange?.(iso);
    }
  };

  const { jour, mois, annee } = choix;
  const maxJour = mois ? joursDuMois(mois, annee) : 31;
  const inexistante = !!(jour && mois && Number(jour) > maxJour);
  const iso = assembler(choix);
  // Comme les anciens min/max : un formulaire validé par le navigateur refuse la date.
  const horsBornes = iso && (iso > String(max).slice(0, 10) ? "Cette date est dans le futur."
    : iso < String(min).slice(0, 10) ? "Vérifie l'année de naissance." : "");
  const validite = inexistante ? "Cette date n'existe pas." : horsBornes || "";

  useEffect(() => {
    refJour.current?.setCustomValidity?.(validite);
  }, [validite]);

  const anneeMax = Number(String(max).slice(0, 4));
  const anneeMin = Number(String(min).slice(0, 4));
  const annees = [];
  for (let a = anneeMax; a >= anneeMin; a -= 1) annees.push(String(a));
  // Une date déjà enregistrée hors de la plage reste visible plutôt que de s'effacer.
  if (annee && !annees.includes(annee)) {
    annees.push(annee);
    annees.sort((x, y) => Number(y) - Number(x));
  }

  const commun = { className: selectClassName, disabled, required, style: { minWidth: 0, paddingLeft: 10, paddingRight: 6, ...selectStyle } };
  const id = (suffixe) => (testid ? `${testid}-${suffixe}` : undefined);

  return (
    <div role="group" aria-label={label} data-testid={testid}>
      <div style={{ display: "grid", gridTemplateColumns: "0.9fr 1.5fr 1.1fr", gap: 6 }}>
        <select {...commun} ref={refJour} value={jour} onChange={choisir("jour")}
          aria-label="Jour de naissance" aria-invalid={inexistante || undefined} data-testid={id("jour")}>
          <option value="">Jour</option>
          {JOURS.map((j) => (
            <option key={j} value={j} disabled={Number(j) > maxJour}>{j}</option>
          ))}
        </select>
        <select {...commun} value={mois} onChange={choisir("mois")}
          aria-label="Mois de naissance" data-testid={id("mois")}>
          <option value="">Mois</option>
          {MOIS_FR.map((m, i) => (
            <option key={m} value={String(i + 1)}>{m}</option>
          ))}
        </select>
        <select {...commun} value={annee} onChange={choisir("annee")}
          aria-label="Année de naissance" data-testid={id("annee")}>
          <option value="">Année</option>
          {annees.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>
      {inexistante && (
        <p className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }} data-testid={id("inexistante")}>
          Le {jour} {MOIS_FR[Number(mois) - 1]}{annee ? ` ${annee}` : ""} n'existe pas : choisis un autre jour.
        </p>
      )}
    </div>
  );
}
